import React, { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Card,
  CardContent,
  GridLegacy as Grid,
  SelectChangeEvent,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { format, subDays, subWeeks, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import api from '../services/api';

// Types
interface RawSheetRecord {
  Date?: string;
  Agent?: string;
  Team?: string;
  Group?: string;
  Calls?: string | number;
  Contacts?: string | number;
  Sales?: string | number;
  Utilization?: string | number;
  'Talk Time'?: string | number;
  ACW?: string | number;
  'Avg Handle Time'?: string | number;
  'Login Time'?: string;
}

interface AgentMetrics {
  agent: string;
  team: string;
  calls: number;
  contacts: number;
  sales: number;
  loginHours: number;
  utilization: number;
  talkPercent: number;
  acwPercent: number;
  aht: number;
  count: number;
  contactRate: number;
  contactsPerHour: number;
  salesPerContact: number;
  sph: number;
  callContactPercent: number;
  contactSalePercent: number;
  overallConvPercent: number;
  callsPerHour: number;
  winRate: number;
  contactsPerSale: number;
  avgUtilization: number;
  avgTalkPercent: number;
  avgAcwPercent: number;
  avgAht: number;
}

interface TeamMetrics {
  team: string;
  calls: number;
  contacts: number;
  sales: number;
  loginHours: number;
  utilization: number;
  talkPercent: number;
  acwPercent: number;
  aht: number;
  count: number;
  contactRate: number;
  contactsPerHour: number;
  salesPerContact: number;
  sph: number;
  callContactPercent: number;
  contactSalePercent: number;
  overallConvPercent: number;
  callsPerHour: number;
  winRate: number;
  contactsPerSale: number;
  avgUtilization: number;
  avgTalkPercent: number;
  avgAcwPercent: number;
  avgAht: number;
}

interface Calculations {
  agents: AgentMetrics[];
  teams: TeamMetrics[];
}

interface SortState {
  [key: string]: 'asc' | 'desc' | undefined;
}

type DateFilter = 'today' | 'currentWeek' | 'previousWeek' | 'currentMonth' | 'previousMonth' | 'dateRange' | 'all';

interface SortableTableCellProps {
  tableId: string;
  field: keyof AgentMetrics | keyof TeamMetrics;
  label: string;
  align?: 'left' | 'right' | 'center';
  children?: React.ReactNode;
  orderBy: { [key: string]: string };
  order: SortState;
  onSort: (tableId: string, property: string) => void;
}

const SortableTableCell: React.FC<SortableTableCellProps> = ({ 
  tableId, 
  field, 
  label, 
  align = 'left', 
  children,
  orderBy,
  order,
  onSort
}) => (
  <TableCell align={align}>
    <TableSortLabel
      active={orderBy[tableId] === field}
      direction={orderBy[tableId] === field ? (order[tableId] || 'asc') : 'asc'}
      onClick={() => onSort(tableId, field)}
    >
      {children || label}
    </TableSortLabel>
  </TableCell>
);

const EnhancedAgentEfficiency: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [dateFilter, setDateFilter] = useState<DateFilter>('currentWeek');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [orderBy, setOrderBy] = useState<{ [key: string]: string }>({});
  const [order, setOrder] = useState<SortState>({});

  const handleSort = useCallback((tableId: string, property: string) => {
    const isAsc = orderBy[tableId] === property && order[tableId] === 'asc';
    setOrder(prev => ({ ...prev, [tableId]: isAsc ? 'desc' : 'asc' }));
    setOrderBy(prev => ({ ...prev, [tableId]: property }));
  }, [orderBy, order]);

  const getSortedData = useCallback(<T extends AgentMetrics | TeamMetrics>(data: T[], tableId: string): T[] => {
    if (!orderBy[tableId]) return data;
    
    const sortProperty = orderBy[tableId] as keyof T;
    const sortOrder = order[tableId] || 'asc';
    
    return [...data].sort((a, b) => {
      let aVal = a[sortProperty];
      let bVal = b[sortProperty];
      
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase() as any;
        bVal = bVal.toLowerCase() as any;
      }
      
      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [orderBy, order]);

  const {
    data: enhancedData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['enhancedAgentEfficiency'],
    queryFn: api.getAllTeamsEfficiencyData,
    refetchInterval: 5 * 60 * 1000,
  });

  const rawData: RawSheetRecord[] = enhancedData?.data?.data || [];

  const parseSheetDate = useCallback((dateString?: string): Date | null => {
    if (!dateString) return null;
    try {
      let dateStr = dateString;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        dateStr = dateString + 'T00:00:00';
      }
      const parsedDate = new Date(dateStr);
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        const currentYear = new Date().getFullYear();
        const parsedYear = parsedDate.getFullYear();
        if (parsedYear >= 2020 && parsedYear <= currentYear + 1) {
          return parsedDate;
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }, []);

  const normalizeDate = useCallback((date?: Date | string | null): string | null => {
    if (!date) return null;
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const filteredData = useMemo(() => {
    if (dateFilter === 'all') return rawData;
    const now = new Date();
    return rawData.filter(record => {
      if (!record.Date) return false;
      const recordDate = parseSheetDate(record.Date);
      if (!recordDate || isNaN(recordDate.getTime())) return false;
      const normalizedRecordDate = normalizeDate(recordDate);
      
      switch (dateFilter) {
        case 'today':
          return normalizedRecordDate === normalizeDate(now);
        case 'currentWeek':
          const weekStart = startOfWeek(now, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
          return normalizedRecordDate! >= normalizeDate(weekStart)! && normalizedRecordDate! <= normalizeDate(weekEnd)!;
        case 'previousWeek':
          const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
          const prevWeekStart = subWeeks(currentWeekStart, 1);
          const prevWeekEnd = subDays(currentWeekStart, 1);
          return normalizedRecordDate! >= normalizeDate(prevWeekStart)! && normalizedRecordDate! <= normalizeDate(prevWeekEnd)!;
        case 'currentMonth':
          return normalizedRecordDate! >= normalizeDate(startOfMonth(now))! && normalizedRecordDate! <= normalizeDate(endOfMonth(now))!;
        case 'previousMonth':
          const prevMonth = subMonths(now, 1);
          return normalizedRecordDate! >= normalizeDate(startOfMonth(prevMonth))! && normalizedRecordDate! <= normalizeDate(endOfMonth(prevMonth))!;
        case 'dateRange':
          if (!startDate || !endDate) return false;
          const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
          const rangeStart = new Date(startYear, startMonth - 1, startDay);
          const rangeEnd = new Date(endYear, endMonth - 1, endDay);
          return normalizedRecordDate! >= normalizeDate(rangeStart)! && normalizedRecordDate! <= normalizeDate(rangeEnd)!;
        default:
          return true;
      }
    });
  }, [rawData, dateFilter, startDate, endDate, parseSheetDate, normalizeDate]);

  const calculations = useMemo((): Calculations => {
    const agentMap: { [key: string]: AgentMetrics } = {};
    const teamMap: { [key: string]: TeamMetrics } = {};

    filteredData.forEach(row => {
      const agent = row.Agent || 'Unknown';
      const team = row.Team || row.Group || 'Unknown';
      
      if (!agentMap[agent]) {
        agentMap[agent] = { 
          agent, team, calls: 0, contacts: 0, sales: 0, loginHours: 0,
          utilization: 0, talkPercent: 0, acwPercent: 0, aht: 0, count: 0,
          contactRate: 0, contactsPerHour: 0, salesPerContact: 0, sph: 0,
          callContactPercent: 0, contactSalePercent: 0, overallConvPercent: 0,
          callsPerHour: 0, winRate: 0, contactsPerSale: 0, avgUtilization: 0,
          avgTalkPercent: 0, avgAcwPercent: 0, avgAht: 0
        };
      }
      
      if (!teamMap[team]) {
        teamMap[team] = { 
          team, calls: 0, contacts: 0, sales: 0, loginHours: 0,
          utilization: 0, talkPercent: 0, acwPercent: 0, aht: 0, count: 0,
          contactRate: 0, contactsPerHour: 0, salesPerContact: 0, sph: 0,
          callContactPercent: 0, contactSalePercent: 0, overallConvPercent: 0,
          callsPerHour: 0, winRate: 0, contactsPerSale: 0, avgUtilization: 0,
          avgTalkPercent: 0, avgAcwPercent: 0, avgAht: 0
        };
      }

      const calls = parseFloat(String(row.Calls || 0));
      const contacts = parseFloat(String(row.Contacts || 0));
      const sales = parseFloat(String(row.Sales || 0));
      const utilization = parseFloat(String(row.Utilization || '0').replace('%', ''));
      const talkPercent = parseFloat(String(row['Talk Time'] || '0').replace('%', ''));
      const acwPercent = parseFloat(String(row['ACW'] || '0').replace('%', ''));
      const aht = parseFloat(String(row['Avg Handle Time'] || 0));
      
      const loginTime = row['Login Time'] || '0:00:00';
      const timeParts = loginTime.split(':');
      const hours = parseInt(timeParts[0]) || 0;
      const minutes = parseInt(timeParts[1]) || 0;
      const loginHours = hours + (minutes / 60);

      agentMap[agent].calls += calls;
      agentMap[agent].contacts += contacts;
      agentMap[agent].sales += sales;
      agentMap[agent].loginHours += loginHours;
      agentMap[agent].utilization += utilization;
      agentMap[agent].talkPercent += talkPercent;
      agentMap[agent].acwPercent += acwPercent;
      agentMap[agent].aht += aht;
      agentMap[agent].count += 1;

      teamMap[team].calls += calls;
      teamMap[team].contacts += contacts;
      teamMap[team].sales += sales;
      teamMap[team].loginHours += loginHours;
      teamMap[team].utilization += utilization;
      teamMap[team].talkPercent += talkPercent;
      teamMap[team].acwPercent += acwPercent;
      teamMap[team].aht += aht;
      teamMap[team].count += 1;
    });

    // Calculate derived metrics
    Object.values(agentMap).forEach(agent => {
      agent.contactRate = agent.calls > 0 ? (agent.contacts / agent.calls) * 100 : 0;
      agent.contactsPerHour = agent.loginHours > 0 ? agent.contacts / agent.loginHours : 0;
      agent.salesPerContact = agent.contacts > 0 ? agent.sales / agent.contacts : 0;
      agent.sph = agent.loginHours > 0 ? agent.sales / agent.loginHours : 0;
      agent.callContactPercent = agent.calls > 0 ? (agent.contacts / agent.calls) * 100 : 0;
      agent.contactSalePercent = agent.contacts > 0 ? (agent.sales / agent.contacts) * 100 : 0;
      agent.overallConvPercent = agent.calls > 0 ? (agent.sales / agent.calls) * 100 : 0;
      agent.callsPerHour = agent.loginHours > 0 ? agent.calls / agent.loginHours : 0;
      agent.winRate = agent.contacts > 0 ? (agent.sales / agent.contacts) * 100 : 0;
      agent.contactsPerSale = agent.sales > 0 ? agent.contacts / agent.sales : 0;
      agent.avgUtilization = agent.count > 0 ? agent.utilization / agent.count : 0;
      agent.avgTalkPercent = agent.count > 0 ? agent.talkPercent / agent.count : 0;
      agent.avgAcwPercent = agent.count > 0 ? agent.acwPercent / agent.count : 0;
      agent.avgAht = agent.count > 0 ? agent.aht / agent.count : 0;
    });

    Object.values(teamMap).forEach(team => {
      team.contactRate = team.calls > 0 ? (team.contacts / team.calls) * 100 : 0;
      team.contactsPerHour = team.loginHours > 0 ? team.contacts / team.loginHours : 0;
      team.salesPerContact = team.contacts > 0 ? team.sales / team.contacts : 0;
      team.sph = team.loginHours > 0 ? team.sales / team.loginHours : 0;
      team.callContactPercent = team.calls > 0 ? (team.contacts / team.calls) * 100 : 0;
      team.contactSalePercent = team.contacts > 0 ? (team.sales / team.contacts) * 100 : 0;
      team.overallConvPercent = team.calls > 0 ? (team.sales / team.calls) * 100 : 0;
      team.callsPerHour = team.loginHours > 0 ? team.calls / team.loginHours : 0;
      team.winRate = team.contacts > 0 ? (team.sales / team.contacts) * 100 : 0;
      team.contactsPerSale = team.sales > 0 ? team.contacts / team.sales : 0;
      team.avgUtilization = team.count > 0 ? team.utilization / team.count : 0;
      team.avgTalkPercent = team.count > 0 ? team.talkPercent / team.count : 0;
      team.avgAcwPercent = team.count > 0 ? team.acwPercent / team.count : 0;
      team.avgAht = team.count > 0 ? team.aht / team.count : 0;
    });

    return { agents: Object.values(agentMap), teams: Object.values(teamMap) };
  }, [filteredData]);

  const getFilterLabel = useCallback((): string => {
    switch (dateFilter) {
      case 'today': return 'Today';
      case 'currentWeek': return 'Current Week';
      case 'previousWeek': return 'Previous Week';
      case 'currentMonth': return 'Current Month';
      case 'previousMonth': return 'Previous Month';
      case 'dateRange':
        if (startDate && endDate) {
          const start = format(new Date(startDate), 'MMM dd, yyyy');
          const end = format(new Date(endDate), 'MMM dd, yyyy');
          return startDate === endDate ? start : `${start} - ${end}`;
        }
        return 'Date Range';
      case 'all': return 'All Time';
      default: return '';
    }
  }, [dateFilter, startDate, endDate]);

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load enhanced efficiency data. Please check your connection and try again.
        </Alert>
      </Container>
    );
  }

  const tabLabels = [
    'Contact Efficiency',
    'Conversion Funnel',
    'Productivity',
    'Win Rate Quality',
    'Time Utilization',
    'Team Comparison',
    'Contact-to-Sale',
    'Quality vs Quantity'
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4">
          Enhanced Agent Efficiency Report
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <Select
              value={dateFilter}
              onChange={(e: SelectChangeEvent<DateFilter>) => setDateFilter(e.target.value as DateFilter)}
              displayEmpty
            >
              <MenuItem value="today">Current Day</MenuItem>
              <MenuItem value="currentWeek">Current Week</MenuItem>
              <MenuItem value="previousWeek">Previous Week</MenuItem>
              <MenuItem value="currentMonth">Current Month</MenuItem>
              <MenuItem value="previousMonth">Previous Month</MenuItem>
              <MenuItem value="dateRange">Date Range</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          {dateFilter === 'dateRange' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">From:</Typography>
              <TextField
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{ minWidth: 150 }}
              />
              <Typography variant="body2">To:</Typography>
              <TextField
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ minWidth: 150 }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Summary Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Calls
                  </Typography>
                  <Typography variant="h4">
                    {calculations.agents.reduce((sum, agent) => sum + agent.calls, 0).toLocaleString()}
                  </Typography>
                </Box>
                <PhoneIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Contacts
                  </Typography>
                  <Typography variant="h4">
                    {calculations.agents.reduce((sum, agent) => sum + agent.contacts, 0).toLocaleString()}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Sales
                  </Typography>
                  <Typography variant="h4">
                    {calculations.agents.reduce((sum, agent) => sum + agent.sales, 0).toLocaleString()}
                  </Typography>
                </Box>
                <TrophyIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Contact Rate
                  </Typography>
                  <Typography variant="h4">
                    {calculations.agents.length > 0
                      ? (calculations.agents.reduce((sum, agent) => sum + agent.contactRate, 0) / calculations.agents.length).toFixed(1)
                      : 0}%
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          {tabLabels.map((label, idx) => (
            <Tab key={idx} label={label} />
          ))}
        </Tabs>
      </Paper>

      <Box>
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Contact Efficiency Report</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Shows how efficiently agents convert calls into actual contacts. Contact Rate = (Contacts / Calls) × 100
            </Typography>
            <Typography variant="body2" paragraph>
              Period: {getFilterLabel()} | Records: {filteredData.length} | Agents: {calculations.agents.length}
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell tableId="tab0" field="agent" label="Agent" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab0" field="calls" label="Calls" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab0" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab0" field="contactRate" label="Contact Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab0" field="contactsPerHour" label="Contacts/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab0" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab0" field="salesPerContact" label="Sales/Contact" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedData(calculations.agents, 'tab0').map((agent, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{agent.agent}</TableCell>
                      <TableCell align="right">{Math.round(agent.calls)}</TableCell>
                      <TableCell align="right">{Math.round(agent.contacts)}</TableCell>
                      <TableCell align="right">{agent.contactRate.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.contactsPerHour.toFixed(2)}</TableCell>
                      <TableCell align="right">{Math.round(agent.sales)}</TableCell>
                      <TableCell align="right">{agent.salesPerContact.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Sales Conversion Funnel</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Identifies where prospects drop off: Calls → Contacts → Sales
            </Typography>
            <Typography variant="body2" paragraph>
              Period: {getFilterLabel()} | Records: {filteredData.length} | Agents: {calculations.agents.length}
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell tableId="tab1" field="agent" label="Agent" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab1" field="calls" label="Calls" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab1" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab1" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab1" field="callContactPercent" label="Call→Contact %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab1" field="contactSalePercent" label="Contact→Sale %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab1" field="overallConvPercent" label="Overall Conv %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedData(calculations.agents, 'tab1').map((agent, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{agent.agent}</TableCell>
                      <TableCell align="right">{Math.round(agent.calls)}</TableCell>
                      <TableCell align="right">{Math.round(agent.contacts)}</TableCell>
                      <TableCell align="right">{Math.round(agent.sales)}</TableCell>
                      <TableCell align="right">{agent.callContactPercent.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.contactSalePercent.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.overallConvPercent.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Agent Productivity Report</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Measures SPH, Contacts per Hour, Calls per Hour - volume efficiency
            </Typography>
            <Typography variant="body2" paragraph>
              Period: {getFilterLabel()} | Records: {filteredData.length} | Agents: {calculations.agents.length}
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell tableId="tab2" field="agent" label="Agent" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab2" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab2" field="sph" label="SPH" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab2" field="contactsPerHour" label="Contacts/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab2" field="callsPerHour" label="Calls/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab2" field="loginHours" label="Login Hours" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedData(calculations.agents.filter(a => a.loginHours > 0), 'tab2').map((agent, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{agent.agent}</TableCell>
                      <TableCell align="right">{Math.round(agent.sales)}</TableCell>
                      <TableCell align="right">{agent.sph.toFixed(2)}</TableCell>
                      <TableCell align="right">{agent.contactsPerHour.toFixed(2)}</TableCell>
                      <TableCell align="right">{agent.callsPerHour.toFixed(2)}</TableCell>
                      <TableCell align="right">{agent.loginHours.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 3 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Win Rate vs Contact Quality</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Determines if higher contact rates lead to better or worse win rates
            </Typography>
            <Typography variant="body2" paragraph>
              Period: {getFilterLabel()} | Records: {filteredData.length} | Agents: {calculations.agents.length}
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell tableId="tab3" field="agent" label="Agent" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab3" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab3" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab3" field="winRate" label="Win Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab3" field="contactsPerSale" label="Contacts/Sale" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab3" field="avgTalkPercent" label="Avg Talk %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedData(calculations.agents.filter(a => a.contacts > 0), 'tab3').map((agent, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{agent.agent}</TableCell>
                      <TableCell align="right">{Math.round(agent.contacts)}</TableCell>
                      <TableCell align="right">{Math.round(agent.sales)}</TableCell>
                      <TableCell align="right">{agent.winRate.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.contactsPerSale.toFixed(2)}</TableCell>
                      <TableCell align="right">{agent.avgTalkPercent.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 4 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Time Utilization Efficiency</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Shows how time allocation (Utilization %, Talk %, ACW %, AHT) impacts results
            </Typography>
            <Typography variant="body2" paragraph>
              Period: {getFilterLabel()} | Records: {filteredData.length} | Agents: {calculations.agents.length}
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell tableId="tab4" field="agent" label="Agent" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab4" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab4" field="avgUtilization" label="Avg Utilization" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab4" field="avgTalkPercent" label="Avg Talk %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab4" field="avgAcwPercent" label="Avg ACW %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab4" field="avgAht" label="Avg AHT" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedData(calculations.agents, 'tab4').map((agent, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{agent.agent}</TableCell>
                      <TableCell align="right">{Math.round(agent.sales)}</TableCell>
                      <TableCell align="right">{agent.avgUtilization.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.avgTalkPercent.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.avgAcwPercent.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.avgAht.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 5 && (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Team Comparative Performance</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Benchmarks BPO, Inside Sales, and SoniqCX teams - identifies best practices
              </Typography>
              <Typography variant="body2" paragraph>
                Period: {getFilterLabel()} | Records: {filteredData.length} | Teams: {calculations.teams.length}
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5a" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5a" field="calls" label="Calls" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5a" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5a" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5a" field="sph" label="SPH" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5a" field="winRate" label="Win Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams, 'tab5a').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.calls)}</TableCell>
                        <TableCell align="right">{Math.round(team.contacts)}</TableCell>
                        <TableCell align="right">{Math.round(team.sales)}</TableCell>
                        <TableCell align="right">{team.sph.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.winRate.toFixed(2)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Contact Efficiency by Team</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Shows how efficiently each team converts calls into contacts
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5b" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5b" field="calls" label="Calls" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5b" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5b" field="contactRate" label="Contact Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5b" field="contactsPerHour" label="Contacts/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5b" field="salesPerContact" label="Sales/Contact" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams, 'tab5b').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.calls)}</TableCell>
                        <TableCell align="right">{Math.round(team.contacts)}</TableCell>
                        <TableCell align="right">{team.contactRate.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.contactsPerHour.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.salesPerContact.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Conversion Funnel by Team</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Identifies where prospects drop off in each team's sales process
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5c" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5c" field="calls" label="Calls" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5c" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5c" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5c" field="callContactPercent" label="Call→Contact %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5c" field="contactSalePercent" label="Contact→Sale %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5c" field="overallConvPercent" label="Overall Conv %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams, 'tab5c').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.calls)}</TableCell>
                        <TableCell align="right">{Math.round(team.contacts)}</TableCell>
                        <TableCell align="right">{Math.round(team.sales)}</TableCell>
                        <TableCell align="right">{team.callContactPercent.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.contactSalePercent.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.overallConvPercent.toFixed(2)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Productivity by Team</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Measures SPH, Contacts per Hour, Calls per Hour for each team
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5d" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5d" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5d" field="sph" label="SPH" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5d" field="contactsPerHour" label="Contacts/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5d" field="callsPerHour" label="Calls/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5d" field="loginHours" label="Login Hours" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams.filter(t => t.loginHours > 0), 'tab5d').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.sales)}</TableCell>
                        <TableCell align="right">{team.sph.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.contactsPerHour.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.callsPerHour.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.loginHours.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Win Rate Quality by Team</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Compares win rates and contact quality across teams
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5e" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5e" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5e" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5e" field="winRate" label="Win Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5e" field="contactsPerSale" label="Contacts/Sale" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5e" field="avgTalkPercent" label="Avg Talk %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams.filter(t => t.contacts > 0), 'tab5e').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.contacts)}</TableCell>
                        <TableCell align="right">{Math.round(team.sales)}</TableCell>
                        <TableCell align="right">{team.winRate.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.contactsPerSale.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.avgTalkPercent.toFixed(2)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Time Utilization by Team</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Shows how time allocation impacts results for each team
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5f" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5f" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5f" field="avgUtilization" label="Avg Utilization" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5f" field="avgTalkPercent" label="Avg Talk %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5f" field="avgAcwPercent" label="Avg ACW %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5f" field="avgAht" label="Avg AHT" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams, 'tab5f').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.sales)}</TableCell>
                        <TableCell align="right">{team.avgUtilization.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.avgTalkPercent.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.avgAcwPercent.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.avgAht.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Contact-to-Sale Efficiency by Team</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Shows efficiency of converting contacts to sales for each team
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5g" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5g" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5g" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5g" field="contactsPerSale" label="Contacts/Sale" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5g" field="winRate" label="Win Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5g" field="sph" label="SPH" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams.filter(t => t.sales > 0), 'tab5g').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.contacts)}</TableCell>
                        <TableCell align="right">{Math.round(team.sales)}</TableCell>
                        <TableCell align="right">{team.contactsPerSale.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.winRate.toFixed(2)}%</TableCell>
                        <TableCell align="right">{team.sph.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Quality vs Quantity by Team</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Optimal balance between call volume and quality for each team
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell tableId="tab5h" field="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5h" field="calls" label="Calls" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5h" field="callsPerHour" label="Calls/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5h" field="avgAht" label="Avg AHT" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5h" field="avgTalkPercent" label="Avg Talk %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5h" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableCell tableId="tab5h" field="winRate" label="Win Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedData(calculations.teams.filter(t => t.loginHours > 0), 'tab5h').map((team, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{team.team}</TableCell>
                        <TableCell align="right">{Math.round(team.calls)}</TableCell>
                        <TableCell align="right">{team.callsPerHour.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.avgAht.toFixed(2)}</TableCell>
                        <TableCell align="right">{team.avgTalkPercent.toFixed(2)}%</TableCell>
                        <TableCell align="right">{Math.round(team.sales)}</TableCell>
                        <TableCell align="right">{team.winRate.toFixed(2)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {activeTab === 6 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Contact-to-Sale Efficiency</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Shows efficiency of converting contacts to sales (different from raw win rate)
            </Typography>
            <Typography variant="body2" paragraph>
              Period: {getFilterLabel()} | Records: {filteredData.length} | Agents: {calculations.agents.length}
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell tableId="tab6" field="agent" label="Agent" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab6" field="contacts" label="Contacts" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab6" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab6" field="contactsPerSale" label="Contacts/Sale" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab6" field="winRate" label="Win Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab6" field="sph" label="SPH" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedData(calculations.agents.filter(a => a.sales > 0), 'tab6').map((agent, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{agent.agent}</TableCell>
                      <TableCell align="right">{Math.round(agent.contacts)}</TableCell>
                      <TableCell align="right">{Math.round(agent.sales)}</TableCell>
                      <TableCell align="right">{agent.contactsPerSale.toFixed(2)}</TableCell>
                      <TableCell align="right">{agent.winRate.toFixed(2)}%</TableCell>
                      <TableCell align="right">{agent.sph.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 7 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Call Quality vs Quantity</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Determines optimal balance between call volume (quantity) and call quality (AHT, Talk %)
            </Typography>
            <Typography variant="body2" paragraph>
              Period: {getFilterLabel()} | Records: {filteredData.length} | Agents: {calculations.agents.length}
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell tableId="tab7" field="agent" label="Agent" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab7" field="calls" label="Calls" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab7" field="callsPerHour" label="Calls/Hour" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab7" field="avgAht" label="Avg AHT" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab7" field="avgTalkPercent" label="Avg Talk %" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab7" field="sales" label="Sales" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                    <SortableTableCell tableId="tab7" field="winRate" label="Win Rate" align="right" orderBy={orderBy} order={order} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedData(calculations.agents.filter(a => a.loginHours > 0), 'tab7').map((agent, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{agent.agent}</TableCell>
                      <TableCell align="right">{Math.round(agent.calls)}</TableCell>
                      <TableCell align="right">{agent.callsPerHour.toFixed(2)}</TableCell>
                      <TableCell align="right">{agent.avgAht.toFixed(2)}</TableCell>
                      <TableCell align="right">{agent.avgTalkPercent.toFixed(2)}%</TableCell>
                      <TableCell align="right">{Math.round(agent.sales)}</TableCell>
                      <TableCell align="right">{agent.winRate.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Chip label={`${filteredData.length} records in view`} color="primary" variant="outlined" />
          <Chip label={`${calculations.agents.length} agents`} color="secondary" variant="outlined" />
          <Chip label={`${calculations.teams.length} teams`} color="info" variant="outlined" />
          <Typography variant="body2" color="textSecondary">
            Real-time sync with Google Sheets
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default EnhancedAgentEfficiency;
